// AppSync GraphQL Types
export enum ChatMessageFromType {
  USER = "USER",
  AGENT = "AGENT",
  TOOL = "TOOL",
}

export enum RunStatus {
  TO_RUN = "TO_RUN",
  // Pre-engine phase: lmnt_pull_workflow has registered the run and is
  // running regen + transforms + scorecard. Engine has not yet been triggered.
  RUN_TRANSFORMS_RUNNING = "RUN_TRANSFORMS_RUNNING",
  RUN_COMPLETED = "RUN_COMPLETED",
  RUN_IN_PROGRESS = "RUN_IN_PROGRESS",
  RUN_FAILED = "RUN_FAILED",
  CANCEL_COMPLETED = "CANCEL_COMPLETED",
  CANCEL_IN_PROGRESS = "CANCEL_IN_PROGRESS",
  CANCEL_FAILED = "CANCEL_FAILED",
}

export interface ChatMessage {
  id: string;
  organizationId: string;
  contextId: string;
  threadId: string;
  fromType: ChatMessageFromType;
  fromId: string;
  message: string;
  createdAt: string;
  updatedAt: string;
}

export interface Run {
  runSettings: string;
  organizationId: string;
  status: RunStatus;
  createdAt: string;
  updatedAt: string;
  stepIds: string;
  initiatedBy?: string | null;
  name?: string | null;
  description?: string | null;
}

export interface ChatMessageConnection {
  items?: ChatMessage[] | null;
  nextToken?: string | null;
}

export enum ChatThreadStatus {
  ACTIVE = "ACTIVE",
  ARCHIVED = "ARCHIVED",
}

export interface ChatThread {
  organizationId: string;
  threadId: string;
  contextId: string;
  runSettings: string;
  status: ChatThreadStatus;
  title?: string | null;
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ChatEvent {
  organizationId: string;
  threadId: string;
  turnId: string;
  eventId: string;
  sequence: number;
  eventType: string;
  payload: string;
  createdAt: string;
}

export interface ChatEventConnection {
  items?: ChatEvent[] | null;
  nextToken?: string | null;
}

export interface RunConnection {
  items?: Run[] | null;
  nextToken?: string | null;
}

// Query input types
export interface GetMyMessagesByContextInput {
  organizationId: string;
  contextId: string;
  limit?: number | null;
  nextToken?: string | null;
}

export interface GetMyRunsInput {
  organizationId: string;
  limit?: number | null;
  nextToken?: string | null;
}

export interface CreateChatThreadInput {
  organizationId: string;
  threadId: string;
  contextId: string;
  runSettings: string;
  status: ChatThreadStatus;
  title?: string | null;
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GetChatThreadInput {
  organizationId: string;
  threadId: string;
}

export interface AppendChatEventInput {
  organizationId: string;
  threadId: string;
  turnId: string;
  eventId: string;
  threadSequence: string;
  sequence: number;
  eventType: string;
  payload: string;
  createdAt: string;
}

export interface GetChatEventsByThreadInput {
  organizationId: string;
  threadId: string;
  limit?: number | null;
  nextToken?: string | null;
}

// Subscription input types
export interface OnNewMessageInput {
  organizationId: string;
  contextId: string;
}

export interface OnChatEventInput {
  organizationId: string;
  threadId: string;
}

export interface OnNewRunInput {
  organizationId: string;
}

export enum ViewTransformStatus {
  IN_PROGRESS = "IN_PROGRESS",
  FAILED = "FAILED",
  COMPLETED = "COMPLETED",
}

export interface ViewTransform {
  id: string;
  organizationId: string;
  transformId: string; // The name/ID of the transform (e.g., "hello_world")
  runSettings: string;
  transformationSettings: string;
  status: ViewTransformStatus;
  s3Url?: string | null;
  errorMessage?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface OnViewTransformUpdateInput {
  organizationId: string;
  /** Required transformId for filtered subscription */
  transformId: string;
  /** Required runSettings for filtered subscription */
  runSettings: string;
  /** Required transformationSettings for filtered subscription */
  transformationSettings: string;
}

// Pull Status types
export enum PullStatusValue {
  SUCCESS = "SUCCESS",
  FAILED = "FAILED",
}

export interface DataPullStatus {
  sourceId: string;
  organizationId: string;
  status: PullStatusValue;
  recordCount?: number | null;
  lastPulledAt: string;
  durationSeconds?: number | null;
  errorMessage?: string | null;
  updatedAt: string;
}

export interface DataPullStatusConnection {
  items?: DataPullStatus[] | null;
  nextToken?: string | null;
}

export interface GetPullStatusesInput {
  organizationId: string;
  limit?: number | null;
  nextToken?: string | null;
}

export interface OnPullStatusUpdateInput {
  organizationId: string;
}
